/**
 * Home-screen additions for Location Service Engine tenants — PRD §9 "Dynamic home screen": the backend says which
 * zone the customer's address is in and what it offers, and the app shows it (no city logic in the app).
 *   * zone strip: zone type, delivery promise ("Delivery tomorrow", "10–30 min"), or Coming Soon / Unavailable.
 *   * Phase 2 "Buy again" rail from the customer's own orders (priced by the server's pricing engine).
 * Renders nothing for any other tenant (and makes no API call).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, RotateCcw, Zap } from "lucide-react";
import { api } from "../../lib/api";
import { ZONE_TYPE_LABEL, inr, useLse } from "../../lib/lse";
import { useDefaultAddress } from "../../lib/storefrontHooks";
import { useAuth } from "../../contexts/AuthContext";

export function LseZoneStrip() {
    const { on } = useLse();
    // The inner component owns the address lookup, so a tenant without the engine makes no extra request.
    return on ? <ZoneStrip /> : null;
}

function ZoneStrip() {
    const addr = useDefaultAddress();
    const [ctx, setCtx] = useState(null);
    useEffect(() => {
        if (!addr) { setCtx(null); return; }
        const qs = new URLSearchParams();
        if (addr.id) qs.set("address_id", addr.id);
        if (addr.pincode) qs.set("pincode", addr.pincode);
        if (addr.lat != null && addr.lng != null) { qs.set("lat", addr.lat); qs.set("lng", addr.lng); }
        api.get(`/lse/context?${qs.toString()}`).then(({ data }) => setCtx(data)).catch(() => setCtx(null));
    }, [addr?.id, addr?.pincode, addr?.lat, addr?.lng]);   // eslint-disable-line react-hooks/exhaustive-deps
    if (!ctx || (!ctx.zone && ctx.serviceable)) return null;
    if (!ctx.serviceable) {
        return (
            <div data-testid="lse-zone-strip" data-state="unserviceable"
                 className="mx-4 mt-3 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800 px-4 py-3 text-sm flex gap-2">
                <MapPin size={16} className="shrink-0 mt-0.5" />
                <span><span className="font-semibold">{ZONE_TYPE_LABEL[ctx.zone?.zone_type] || "Not serviceable"}</span> — {ctx.message}</span>
            </div>
        );
    }
    const fast = (ctx.delivery_options || []).find((o) => o.available && ["INSTANT", "EXPRESS"].includes(o.mode));
    const Icon = fast ? Zap : Clock;
    return (
        <div data-testid="lse-zone-strip" data-state="serviceable"
             className="mx-4 mt-3 rounded-2xl bg-primary/10 text-foreground px-4 py-2.5 text-sm flex items-center gap-2 min-w-0">
            <Icon size={16} className="text-primary shrink-0" />
            <span className="font-semibold truncate">{ctx.promise || "Delivering to your area"}</span>
            {ctx.zone?.zone_type && <span className="ml-auto shrink-0 text-[11px] font-semibold text-muted-foreground">{ZONE_TYPE_LABEL[ctx.zone.zone_type]}</span>}
        </div>
    );
}

export function LseBuyAgainRail() {
    const { has } = useLse();
    const { user } = useAuth();
    const on = has("recommendations") && !!user;
    const [rows, setRows] = useState(null);
    useEffect(() => {
        if (!on) return;
        api.get("/lse/recommendations/buy-again").then(({ data }) => setRows(data.products || [])).catch(() => setRows([]));
    }, [on]);
    if (!on || !rows?.length) return null;
    return (
        <section className="mt-5" data-testid="lse-buy-again">
            <h2 className="px-4 text-base font-bold inline-flex items-center gap-1.5"><RotateCcw size={16} className="text-primary" /> Buy again</h2>
            <div className="mt-2 flex gap-3 overflow-x-auto px-4 pb-1 no-scrollbar">
                {rows.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} data-testid={`lse-buy-again-${p.id}`}
                          className="shrink-0 w-[128px] rounded-2xl border border-border bg-card p-2">
                        <div className="aspect-square rounded-xl bg-muted overflow-hidden">
                            {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" className="w-full h-full object-cover" />}
                        </div>
                        <p className="mt-1.5 text-xs font-semibold line-clamp-2 min-h-[2rem]">{p.name}</p>
                        <p className="text-xs font-bold mt-0.5">{inr(p.selling_price)}</p>
                    </Link>
                ))}
            </div>
        </section>
    );
}

export default function LseHome() {
    return (<><LseZoneStrip /><LseBuyAgainRail /></>);
}
